import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Box, Paper, CircularProgress, Alert, Tabs, Tab, Badge, Snackbar } from '@mui/material';

// Hooks
import { useAuth } from '../components/AuthContext';
import { useObraDetails } from '../hooks/useObraDetails';
import { useObraCustos } from '../hooks/useObraCustos';
import { useLancamentos } from '../hooks/useLancamentos';
import { useValoresCorrigidos } from '../hooks/useValoresCorrigidos';
import { useObraTabs } from '../hooks/useObraTabs';
import { useObraModals } from '../hooks/useObraModals';
import { useObraActions } from '../hooks/useObraActions';

// Components
import { ObraHeader } from '../components/obra-details/ObraHeader';
import { ObraModals } from '../components/obra-details/ObraModals';
import { ObraTabsContent } from '../components/obra-details/ObraTabsContent';

const ObraDetailsPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { role } = useAuth();
    const isAdmin = role === 'admin' || role === 'Administrador';

    // 1. Data Fetching
    const { obra, balanco, loading: loadingDetails, error: errorDetails, refetch: refetchDetails } = useObraDetails(id);
    const { valoresCorrigidos, recalculate: recalculateCorrecao } = useValoresCorrigidos(obra, balanco);

    // 2. Local State & Custom Hooks
    // 2. Local State & Custom Hooks
    const {
        activeTab, activeAnaliseSubTab, setActiveAnaliseSubTab, breadcrumbs, selectedInfo,
        handleTabChange, handleItemClick, handleBreadcrumbClick
    } = useObraTabs(id);

    const modalsState = useObraModals();
    const {
        setSnackbarMessage, snackbarMessage,
        setIsDeleteModalOpen
    } = modalsState; // Destructure what we need for actions/page

    // We need a separate refresh trigger for lancamentos that can be passed to actions
    const [refreshLancamentosTrigger, setRefreshLancamentosTrigger] = useState(0);

    const actions = useObraActions(
        id,
        obra?.nome,
        refetchDetails,
        setSnackbarMessage,
        setIsDeleteModalOpen,
        setRefreshLancamentosTrigger
    );

    // 3. Derived State for Custos
    const parentCategory = breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1] : null;

    // Determine groupBy based on active tab and sub-tab
    let groupBy = 'categoria';
    if (activeTab === 'analise') {
        groupBy = activeAnaliseSubTab;
    } else if (activeTab === 'analise_categoria') { // Fallback for old state if any
        groupBy = 'categoria';
    }

    const categoriaPaiId = (activeTab === 'analise' && activeAnaliseSubTab === 'categoria' && parentCategory) ? parentCategory.id : undefined;
    const isAnaliseTab = activeTab === 'analise';

    const { custos, loading: loadingCustos, error: errorCustos } = useObraCustos(
        id,
        groupBy,
        categoriaPaiId,
        isAnaliseTab && !selectedInfo
    );

    // 4. Derived State for Lancamentos (Drill-down)
    const drillDownFilters = {
        search: '',
        startDate: '',
        endDate: '',
        categoriaId: (activeTab === 'analise' && activeAnaliseSubTab === 'categoria' && selectedInfo) ? selectedInfo.id : undefined,
        faseId: (activeTab === 'analise' && activeAnaliseSubTab === 'fase' && selectedInfo) ? selectedInfo.id : undefined,
        fornecedorId: (activeTab === 'analise' && activeAnaliseSubTab === 'fornecedor' && selectedInfo) ? selectedInfo.id : undefined
    };

    const { lancamentos: drillDownLancamentos } = useLancamentos(
        id,
        drillDownFilters,
        !!selectedInfo,
        refreshLancamentosTrigger
    );

    // 5. Fetch count for "Pagamentos sem Comprovação" badge
    const { total: totalSemComprovacao } = useLancamentos(
        id,
        { search: '', startDate: '', endDate: '', missingAttachments: true, page: 0, pageSize: 1 },
        true,
        refreshLancamentosTrigger
    );

    // 6. Main Lancamentos Tab Filter State
    const [lancamentosFilter, setLancamentosFilter] = useState({ search: '', startDate: '', endDate: '' });


    if (loadingDetails && !balanco) return <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>;
    if (errorDetails) return <Alert severity="error" sx={{ m: 2 }}>{errorDetails}</Alert>;
    if (!obra) return <Alert severity="info" sx={{ m: 2 }}>Nenhum dado encontrado para esta obra.</Alert>;

    return (
        <Container maxWidth="xl">
            <ObraHeader
                obra={obra}
                isAdmin={isAdmin}
                onEdit={() => modalsState.setIsEditModalOpen(true)}
                onDelete={() => modalsState.setIsDeleteModalOpen(true)}
            />

            {actions.deleteError && <Alert severity="error" sx={{ mb: 2 }}>{actions.deleteError}</Alert>}
            {errorCustos && <Alert severity="error" sx={{ mb: 2 }}>Erro ao carregar custos: {errorCustos}</Alert>}

            <Paper>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        aria-label="abas detalhes obra"
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                    >
                        <Tab label="Balanço" value="balanco" />
                        <Tab label="Orçamento" value="orcamento" />
                        <Tab label="Cronograma" value="cronograma" />
                        <Tab label="Contratos" value="contratos" />
                        <Tab label="Análise" value="analise" />
                        <Tab label="Lançamentos" value="lancamentos" />
                        <Tab
                            label={
                                <Badge badgeContent={totalSemComprovacao} color="error">
                                    Pagamentos sem Comprovação
                                </Badge>
                            }
                            value="pagamentos_sem_comprovacao"
                        />
                        <Tab label="Relatório Quantitativo" value="relatorio_quantitativo" />
                        <Tab label="Documentação" value="documentacao" />
                        {isAdmin && <Tab label="Usuários Responsáveis" value="usuarios" />}
                    </Tabs>
                </Box>

                <ObraTabsContent
                    activeTab={activeTab}
                    activeAnaliseSubTab={activeAnaliseSubTab}
                    setActiveAnaliseSubTab={setActiveAnaliseSubTab}
                    obraId={Number(id)}
                    balanco={balanco}
                    obra={obra}
                    valoresCorrigidos={valoresCorrigidos}
                    custos={custos}
                    loadingCustos={loadingCustos}
                    drillDownLancamentos={drillDownLancamentos}
                    breadcrumbs={breadcrumbs}
                    selectedInfo={selectedInfo}
                    handleItemClick={handleItemClick}
                    handleBreadcrumbClick={handleBreadcrumbClick}
                    openAttachments={modalsState.openAttachments}
                    lancamentosFilter={lancamentosFilter}
                    setLancamentosFilter={setLancamentosFilter}
                    openLancamentoEdit={modalsState.openLancamentoEdit}
                    handleLancamentoDelete={actions.handleLancamentoDelete}
                    refreshLancamentosTrigger={refreshLancamentosTrigger}
                    setIsValoresCorrigidosModalOpen={modalsState.setIsValoresCorrigidosModalOpen}
                />
            </Paper>

            <ObraModals
                obra={obra}
                modalsState={modalsState}
                actions={actions}
                refetchDetails={refetchDetails}
                valoresCorrigidos={valoresCorrigidos}
                recalculateCorrecao={recalculateCorrecao}
                setRefreshLancamentosTrigger={setRefreshLancamentosTrigger}
            />

            <Snackbar
                open={!!snackbarMessage}
                autoHideDuration={6000}
                onClose={() => setSnackbarMessage(null)}
                message={snackbarMessage}
            />
        </Container>
    );
};

export default ObraDetailsPage;